import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { FILTER_GAMES } from 'src/common/filter-games'

export const useUserBonusesStore = defineStore('userBonuses', () => {
  const freeSpins = ref([])
  const freeBets = ref(null)
  const programsAvailable = ref([])
  const activeStep = ref(null)
  const activeStepLoaded = ref(false)
  const activeUserProgram = ref(null)
  const conditionsChoices = ref(null)
  const bonusGameIdsList = ref(null)
  const freeSpinEnd = ref(null)
  const currentLiveCashBack = ref(null)

  const getActiveUserProgram = computed(() => {
    return programsAvailable.value.find((el) => el.isActivated)
  })
  const getBonusGameIdsList = computed(() => {
    return bonusGameIdsList.value ?? []
  })
  const getProgramsAvailable = computed(() => {
    return programsAvailable.value.filter((el) => !el.isActivated) || []
  })
  const getActiveStep = computed(() => {
    return activeStep.value
  })

  const getConditionStep = computed(() => {
    return activeStep.value?.condition
  })

  const getFreeSpins = computed(() => {
    return freeSpins.value ?? []
  })
  const getFreeBets = computed(() => {
    return freeBets.value
  })
  const getActiveStepLoaded = computed(() => {
    return activeStepLoaded.value
  })
  const showBonusesGames = computed(() => {
    if (!activeStep.value) {
      return false
    }

    const isCasinoSection = getActiveUserProgram.value?.program?.section === 0
    const isVagerByBonusBalance = activeStep.value?.condition === 6

    return isCasinoSection && isVagerByBonusBalance
  })

  function hasFreespinsOnGame({ manufacturer, game }) {
    const activeFreespins = getFreeSpins.value.filter((bonus) => bonus.activated)
    if (!activeFreespins.length) {
      return false
    }

    return !!activeFreespins.find((bonus) => {
      return manufacturer === bonus.game.manufacturer.code && game === bonus.game.slug
    })
  }

  const getBonusType = computed(() => {
    const filters = activeStep.value?.filters

    if (filters?.some((i) => i.type === FILTER_GAMES.ONLY_SPORT)) {
      return 'Sports'
    }

    return 'BonusGames'
  })
  const getConditionsChoices = computed(() => {
    return conditionsChoices.value ?? []
  })
  const countOfBonuses = computed(() => {
    let count = getProgramsAvailable.value?.length
    if (getActiveStep.value && getActiveStep.value?.condition !== 7) {
      count++
    }
    if (getFreeBets.value) {
      count++
    }
    if (freeSpins.value) {
      count = freeSpins.value?.length + count
    }

    return count
  })

  function updateProgramAvailable(payload) {
    programsAvailable.value = payload
  }

  function updateActiveStep(payload) {
    activeStep.value = payload
    activeStepLoaded.value = true
  }

  function updateActiveStepLoaded() {
    activeStepLoaded.value = true
  }

  function deleteUserProgram(payload) {
    if (!getActiveUserProgram.value) {
      return
    }
    if (getActiveUserProgram.value.id === +payload.userProgramId) {
      bonusGameIdsList.value = null
      activeStep.value = null
    }
    programsAvailable.value = programsAvailable.value.filter((el) => +el.id !== +payload.userProgramId)
  }

  function updateUserProgram(payload) {
    if (programsAvailable.value.some((el) => +el.id === +payload.id)) {
      programsAvailable.value = programsAvailable.value.map((program) => {
        return +program.id === +payload.id ? payload : program
      })
    } else {
      programsAvailable.value = [...programsAvailable.value, payload]
    }
  }

  function updateProgram(payload) {
    programsAvailable.value = programsAvailable.value.reduce((programsAvailable, userProgram) => {
      if (+userProgram?.program.id === +payload.id) {
        programsAvailable.push({ ...userProgram, program: { ...userProgram.program, ...payload } })
        return programsAvailable
      }
      programsAvailable.push(userProgram)
      return programsAvailable
    }, [])
  }

  function updateUserStepBalance(payload) {
    if (activeStep.value?.balance !== undefined && payload?.user_step_id === activeStep.value?.id) {
      activeStep.value = { ...activeStep.value, balance: payload.balance }
    }
  }

  function updateFreeBets(payload) {
    freeBets.value = payload
  }

  function updateCurrentLiveCashBack(payload) {
    currentLiveCashBack.value = payload
  }

  function removeFreespinByID(payload) {
    freeSpins.value = freeSpins.value.filter((el) => el.id !== payload)
  }

  function updatePromoFreespinAvailable(payload) {
    const freeSpinsLocal = [...payload]

    if (payload.length === 0 && freeSpins.value?.length !== 0) {
      freeSpinEnd.value = true
    }

    freeSpins.value = freeSpinsLocal.sort((a, b) => b.activated - a.activated)
  }

  function updateConditionsChoices(payload) {
    conditionsChoices.value = payload
  }

  function updateBonusGameIdsList(payload) {
    bonusGameIdsList.value = payload
  }

  function updateFreespinEnd(payload) {
    freeSpinEnd.value = payload
  }

  return {
    // refs
    freeSpins,
    freeBets,
    programsAvailable,
    activeStep,
    activeStepLoaded,
    activeUserProgram,
    conditionsChoices,
    bonusGameIdsList,
    freeSpinEnd,
    currentLiveCashBack,
    // computed
    getActiveUserProgram,
    getBonusGameIdsList,
    getProgramsAvailable,
    getConditionsChoices,
    countOfBonuses,
    getActiveStep,
    getConditionStep,
    getBonusType,
    getFreeSpins,
    getFreeBets,
    getActiveStepLoaded,
    showBonusesGames,
    //functions
    updateProgramAvailable,
    updateActiveStepLoaded,
    updateActiveStep,
    updateUserProgram,
    deleteUserProgram,
    updateProgram,
    updateUserStepBalance,
    updateFreeBets,
    updateCurrentLiveCashBack,
    removeFreespinByID,
    updatePromoFreespinAvailable,
    updateConditionsChoices,
    updateBonusGameIdsList,
    updateFreespinEnd,
    hasFreespinsOnGame,
  }
})
